import { useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Separator } from "@/components/ui/separator";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  CheckCircle2,
  Circle,
  Package,
  Warehouse,
  MapPin,
  Users,
  ShoppingCart,
  TrendingUp,
  FileText,
  Settings,
  PlayCircle,
  BookOpen,
  Lightbulb,
  ArrowRight,
  ChevronRight,
  Sparkles,
  Target,
  Zap,
  BarChart3,
  ClipboardCheck,
} from "lucide-react";

interface OnboardingStep {
  id: string;
  title: string;
  description: string;
  icon: React.ReactNode;
  page: string;
  completed: boolean;
  category: "setup" | "operations" | "advanced";
}

export function QuickStartPage() {
  const [selectedStep, setSelectedStep] = useState<OnboardingStep | null>(null);
  const [showTour, setShowTour] = useState(false);

  const [steps, setSteps] = useState<OnboardingStep[]>([
    {
      id: "warehouses",
      title: "Set Up Warehouses",
      description: "Create and configure your warehouse locations",
      icon: <Warehouse className="h-5 w-5" />,
      page: "warehouses-locations",
      completed: false,
      category: "setup",
    },
    {
      id: "locations",
      title: "Define Storage Locations",
      description: "Organize your warehouse with specific storage locations",
      icon: <MapPin className="h-5 w-5" />,
      page: "warehouses-locations",
      completed: false,
      category: "setup",
    },
    {
      id: "stock-items",
      title: "Add Stock Items",
      description: "Create your product catalog and stock items",
      icon: <Package className="h-5 w-5" />,
      page: "stock-items",
      completed: false,
      category: "setup",
    },
    {
      id: "suppliers",
      title: "Add Suppliers",
      description: "Set up your supplier database",
      icon: <Users className="h-5 w-5" />,
      page: "suppliers",
      completed: false,
      category: "setup",
    },
    {
      id: "customers",
      title: "Add Customers",
      description: "Configure your customer information",
      icon: <Users className="h-5 w-5" />,
      page: "customers",
      completed: false,
      category: "setup",
    },
    {
      id: "purchase-orders",
      title: "Create Purchase Orders",
      description: "Start ordering stock from suppliers",
      icon: <ShoppingCart className="h-5 w-5" />,
      page: "purchase-orders",
      completed: false,
      category: "operations",
    },
    {
      id: "goods-in",
      title: "Receive Goods",
      description: "Process incoming stock deliveries",
      icon: <ClipboardCheck className="h-5 w-5" />,
      page: "goods-in",
      completed: false,
      category: "operations",
    },
    {
      id: "stock-transfers",
      title: "Transfer Stock",
      description: "Move stock between locations",
      icon: <ArrowRight className="h-5 w-5" />,
      page: "stock-transfers",
      completed: false,
      category: "operations",
    },
    {
      id: "sales-orders",
      title: "Process Sales Orders",
      description: "Manage customer orders and fulfillment",
      icon: <FileText className="h-5 w-5" />,
      page: "sales-orders",
      completed: false,
      category: "operations",
    },
    {
      id: "stock-takes",
      title: "Perform Stock Takes",
      description: "Conduct inventory counts and audits",
      icon: <ClipboardCheck className="h-5 w-5" />,
      page: "stock-takes",
      completed: false,
      category: "advanced",
    },
    {
      id: "reports",
      title: "View Reports & Analytics",
      description: "Monitor your inventory performance",
      icon: <BarChart3 className="h-5 w-5" />,
      page: "dashboard",
      completed: false,
      category: "advanced",
    },
  ]);

  const toggleStepCompletion = (stepId: string) => {
    setSteps(
      steps.map((step) =>
        step.id === stepId ? { ...step, completed: !step.completed } : step
      )
    );
  };

  const setupSteps = steps.filter((s) => s.category === "setup");
  const operationsSteps = steps.filter((s) => s.category === "operations");
  const advancedSteps = steps.filter((s) => s.category === "advanced");

  const completedCount = steps.filter((s) => s.completed).length;
  const totalCount = steps.length;
  const progressPercentage = (completedCount / totalCount) * 100;

  const handleNavigate = (page: string) => {
    // This would trigger navigation in the actual app
    window.dispatchEvent(new CustomEvent("navigate", { detail: { page } }));
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <div className="flex items-center gap-3">
          <div className="p-2 bg-primary/10 rounded-lg">
            <Sparkles className="h-6 w-6 text-primary" />
          </div>
          <div>
            <h1 className="text-3xl font-bold tracking-tight">Quick Start Guide</h1>
            <p className="text-muted-foreground mt-1">
              Get up and running with Stock Evo in minutes
            </p>
          </div>
        </div>
      </div>

      {/* Progress Overview */}
      <Card className="border-primary/20 bg-gradient-to-br from-primary/5 to-transparent">
        <CardContent className="pt-6">
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold">Your Progress</h3>
                <p className="text-sm text-muted-foreground">
                  {completedCount} of {totalCount} steps completed
                </p>
              </div>
              <div className="text-right">
                <div className="text-3xl font-bold text-primary">
                  {Math.round(progressPercentage)}%
                </div>
                <p className="text-xs text-muted-foreground">Complete</p>
              </div>
            </div>
            <Progress value={progressPercentage} className="h-3" />
            {completedCount === totalCount && (
              <div className="flex items-center gap-2 text-green-600 dark:text-green-400">
                <CheckCircle2 className="h-5 w-5" />
                <span className="font-medium">Congratulations! You've completed all steps!</span>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Quick Actions */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="hover:shadow-md transition-shadow cursor-pointer" onClick={() => setShowTour(true)}>
          <CardContent className="pt-6">
            <div className="flex items-start gap-4">
              <div className="p-2 bg-blue-100 dark:bg-blue-900 rounded-lg">
                <PlayCircle className="h-6 w-6 text-blue-600 dark:text-blue-400" />
              </div>
              <div>
                <h3 className="font-semibold mb-1">Take a Tour</h3>
                <p className="text-sm text-muted-foreground">
                  Learn the basics with an interactive walkthrough
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="hover:shadow-md transition-shadow cursor-pointer">
          <CardContent className="pt-6">
            <div className="flex items-start gap-4">
              <div className="p-2 bg-purple-100 dark:bg-purple-900 rounded-lg">
                <BookOpen className="h-6 w-6 text-purple-600 dark:text-purple-400" />
              </div>
              <div>
                <h3 className="font-semibold mb-1">Documentation</h3>
                <p className="text-sm text-muted-foreground">
                  Explore detailed guides and best practices
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="hover:shadow-md transition-shadow cursor-pointer">
          <CardContent className="pt-6">
            <div className="flex items-start gap-4">
              <div className="p-2 bg-amber-100 dark:bg-amber-900 rounded-lg">
                <Lightbulb className="h-6 w-6 text-amber-600 dark:text-amber-400" />
              </div>
              <div>
                <h3 className="font-semibold mb-1">Tips & Tricks</h3>
                <p className="text-sm text-muted-foreground">
                  Discover advanced features and shortcuts
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Setup Steps */}
      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <Target className="h-5 w-5 text-primary" />
            <CardTitle>Initial Setup</CardTitle>
          </div>
          <CardDescription>
            Essential configuration to get your system ready
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {setupSteps.map((step, index) => (
              <div
                key={step.id}
                className="flex items-center gap-4 p-4 rounded-lg border hover:bg-accent/50 transition-colors cursor-pointer"
                onClick={() => setSelectedStep(step)}
              >
                <div className="flex items-center gap-3 flex-1">
                  <div
                    className={`p-2 rounded-lg ${
                      step.completed
                        ? "bg-green-100 dark:bg-green-900"
                        : "bg-muted"
                    }`}
                  >
                    {step.completed ? (
                      <CheckCircle2 className="h-5 w-5 text-green-600 dark:text-green-400" />
                    ) : (
                      <Circle className="h-5 w-5 text-muted-foreground" />
                    )}
                  </div>
                  <div className="flex items-center gap-3 flex-1">
                    <div className="p-2 bg-primary/10 rounded-lg">
                      {step.icon}
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <h4 className="font-medium">{step.title}</h4>
                        {step.completed && (
                          <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
                            Done
                          </Badge>
                        )}
                      </div>
                      <p className="text-sm text-muted-foreground">
                        {step.description}
                      </p>
                    </div>
                  </div>
                </div>
                <ChevronRight className="h-5 w-5 text-muted-foreground" />
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Operations Steps */}
      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <Zap className="h-5 w-5 text-primary" />
            <CardTitle>Daily Operations</CardTitle>
          </div>
          <CardDescription>
            Core workflows for managing your inventory
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {operationsSteps.map((step) => (
              <div
                key={step.id}
                className="flex items-center gap-4 p-4 rounded-lg border hover:bg-accent/50 transition-colors cursor-pointer"
                onClick={() => setSelectedStep(step)}
              >
                <div className="flex items-center gap-3 flex-1">
                  <div
                    className={`p-2 rounded-lg ${
                      step.completed
                        ? "bg-green-100 dark:bg-green-900"
                        : "bg-muted"
                    }`}
                  >
                    {step.completed ? (
                      <CheckCircle2 className="h-5 w-5 text-green-600 dark:text-green-400" />
                    ) : (
                      <Circle className="h-5 w-5 text-muted-foreground" />
                    )}
                  </div>
                  <div className="flex items-center gap-3 flex-1">
                    <div className="p-2 bg-primary/10 rounded-lg">
                      {step.icon}
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <h4 className="font-medium">{step.title}</h4>
                        {step.completed && (
                          <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
                            Done
                          </Badge>
                        )}
                      </div>
                      <p className="text-sm text-muted-foreground">
                        {step.description}
                      </p>
                    </div>
                  </div>
                </div>
                <ChevronRight className="h-5 w-5 text-muted-foreground" />
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Advanced Steps */}
      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <TrendingUp className="h-5 w-5 text-primary" />
            <CardTitle>Advanced Features</CardTitle>
          </div>
          <CardDescription>
            Optimize and analyze your inventory management
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {advancedSteps.map((step) => (
              <div
                key={step.id}
                className="flex items-center gap-4 p-4 rounded-lg border hover:bg-accent/50 transition-colors cursor-pointer"
                onClick={() => setSelectedStep(step)}
              >
                <div className="flex items-center gap-3 flex-1">
                  <div
                    className={`p-2 rounded-lg ${
                      step.completed
                        ? "bg-green-100 dark:bg-green-900"
                        : "bg-muted"
                    }`}
                  >
                    {step.completed ? (
                      <CheckCircle2 className="h-5 w-5 text-green-600 dark:text-green-400" />
                    ) : (
                      <Circle className="h-5 w-5 text-muted-foreground" />
                    )}
                  </div>
                  <div className="flex items-center gap-3 flex-1">
                    <div className="p-2 bg-primary/10 rounded-lg">
                      {step.icon}
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <h4 className="font-medium">{step.title}</h4>
                        {step.completed && (
                          <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
                            Done
                          </Badge>
                        )}
                      </div>
                      <p className="text-sm text-muted-foreground">
                        {step.description}
                      </p>
                    </div>
                  </div>
                </div>
                <ChevronRight className="h-5 w-5 text-muted-foreground" />
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Step Detail Dialog */}
      <Dialog open={selectedStep !== null} onOpenChange={() => setSelectedStep(null)}>
        <DialogContent className="max-w-2xl">
          {selectedStep && (
            <>
              <DialogHeader>
                <div className="flex items-center gap-3 mb-2">
                  <div className="p-3 bg-primary/10 rounded-lg">
                    {selectedStep.icon}
                  </div>
                  <div>
                    <DialogTitle className="text-2xl">{selectedStep.title}</DialogTitle>
                    <DialogDescription className="text-base mt-1">
                      {selectedStep.description}
                    </DialogDescription>
                  </div>
                </div>
              </DialogHeader>

              <div className="space-y-4 py-4">
                <Separator />

                <div>
                  <h4 className="font-semibold mb-2 flex items-center gap-2">
                    <Lightbulb className="h-4 w-4 text-amber-500" />
                    What you'll learn
                  </h4>
                  <ul className="space-y-2 text-sm text-muted-foreground">
                    <li className="flex items-start gap-2">
                      <CheckCircle2 className="h-4 w-4 text-green-500 mt-0.5 flex-shrink-0" />
                      <span>How to navigate to the {selectedStep.title.toLowerCase()} section</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle2 className="h-4 w-4 text-green-500 mt-0.5 flex-shrink-0" />
                      <span>Key features and functionality available</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle2 className="h-4 w-4 text-green-500 mt-0.5 flex-shrink-0" />
                      <span>Best practices for {selectedStep.title.toLowerCase()}</span>
                    </li>
                  </ul>
                </div>

                <Separator />

                <div>
                  <h4 className="font-semibold mb-2 flex items-center gap-2">
                    <Target className="h-4 w-4 text-primary" />
                    Quick Tips
                  </h4>
                  <div className="bg-blue-50 dark:bg-blue-950 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
                    <p className="text-sm text-blue-900 dark:text-blue-100">
                      {selectedStep.category === "setup" &&
                        "Complete this step early to ensure smooth operations later. You can always come back and make adjustments."}
                      {selectedStep.category === "operations" &&
                        "This is a core workflow you'll use regularly. Take time to familiarize yourself with all available options."}
                      {selectedStep.category === "advanced" &&
                        "These features help you optimize and gain insights. They're optional but highly recommended for efficient inventory management."}
                    </p>
                  </div>
                </div>
              </div>

              <div className="flex items-center justify-between pt-4 border-t">
                <Button
                  variant="outline"
                  onClick={() => {
                    toggleStepCompletion(selectedStep.id);
                  }}
                >
                  {selectedStep.completed ? (
                    <>
                      <Circle className="h-4 w-4 mr-2" />
                      Mark as Incomplete
                    </>
                  ) : (
                    <>
                      <CheckCircle2 className="h-4 w-4 mr-2" />
                      Mark as Complete
                    </>
                  )}
                </Button>
                <Button
                  onClick={() => {
                    handleNavigate(selectedStep.page);
                    setSelectedStep(null);
                  }}
                >
                  Go to {selectedStep.title}
                  <ArrowRight className="h-4 w-4 ml-2" />
                </Button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Tour Dialog */}
      <Dialog open={showTour} onOpenChange={setShowTour}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <div className="flex items-center gap-3 mb-2">
              <div className="p-3 bg-blue-100 dark:bg-blue-900 rounded-lg">
                <PlayCircle className="h-6 w-6 text-blue-600 dark:text-blue-400" />
              </div>
              <div>
                <DialogTitle className="text-2xl">Welcome to Stock Evo!</DialogTitle>
                <DialogDescription className="text-base mt-1">
                  Let's get you started with the essentials
                </DialogDescription>
              </div>
            </div>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="bg-gradient-to-br from-primary/10 to-transparent rounded-lg p-6 space-y-4">
              <h3 className="text-lg font-semibold">What is Stock Evo?</h3>
              <p className="text-sm text-muted-foreground">
                Stock Evo is an intelligent stock management system designed to help you:
              </p>
              <ul className="space-y-2 text-sm">
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
                  <span>Track inventory across multiple warehouses and locations</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
                  <span>Manage purchase and sales orders efficiently</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
                  <span>Perform accurate stock takes and audits</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
                  <span>Gain insights with powerful analytics and reporting</span>
                </li>
              </ul>
            </div>

            <Separator />

            <div>
              <h4 className="font-semibold mb-3">Getting Started</h4>
              <div className="space-y-3">
                <div className="flex items-start gap-3 p-3 bg-muted rounded-lg">
                  <div className="p-2 bg-background rounded">
                    <Settings className="h-4 w-4" />
                  </div>
                  <div>
                    <p className="font-medium text-sm">1. Configure Your System</p>
                    <p className="text-xs text-muted-foreground">
                      Set up warehouses, locations, and stock items
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-3 p-3 bg-muted rounded-lg">
                  <div className="p-2 bg-background rounded">
                    <Zap className="h-4 w-4" />
                  </div>
                  <div>
                    <p className="font-medium text-sm">2. Start Operations</p>
                    <p className="text-xs text-muted-foreground">
                      Process orders, receive goods, and transfer stock
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-3 p-3 bg-muted rounded-lg">
                  <div className="p-2 bg-background rounded">
                    <BarChart3 className="h-4 w-4" />
                  </div>
                  <div>
                    <p className="font-medium text-sm">3. Monitor & Optimize</p>
                    <p className="text-xs text-muted-foreground">
                      Use reports and analytics to improve efficiency
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="flex justify-end pt-4 border-t">
            <Button onClick={() => setShowTour(false)}>
              Get Started
              <ArrowRight className="h-4 w-4 ml-2" />
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
